import {createI18n} from 'vue-i18n';
import {
  applyI18nLocale,
  loadLocaleMessagesFromGlob,
  normalizeI18nLocale,
} from '../../../../../i18n-glob.js';
import enGBMessages from '../../locales/en-GB.json';

const FALLBACK_LOCALE = 'en-GB';

const europeDateFormat = {
  longSeconds: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  },
  long: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  short: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  },
};

const datetimeFormats = {
  'fr-BE': europeDateFormat,
  'nl-BE': europeDateFormat,
  'de-BE': europeDateFormat,
  'en-GB': europeDateFormat,
};

const numberFormats = {
  'en-GB': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'fr-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'de-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
  'nl-BE': {
    useGrouping: true,
    price: {
      style: 'currency',
      currency: 'EUR',
    },
    priceWithoutDecimal: {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    },
    decimal0Digits: {
      type: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal1Digits: {
      type: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    decimal2Digits: {
      type: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    decimal3Digits: {
      type: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    },
    decimal4Digits: {
      type: 'decimal',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    },
  },
};

//needs to be the same as hit-base language store
export const availableLocales = ['fr-BE', 'nl-BE', 'de-BE', 'mr-IN', 'en-GB'];

/** Lazy loaders — JSON is fetched only when a locale is requested. */
const localeModules = import.meta.glob('../../locales/**/*.json');
const loadedLocales = new Set([FALLBACK_LOCALE]);

export const getLocale = () => {
  let locale =
    window.navigator.userLanguage ||
    window.navigator.language ||
    process.env.VUE_APP_I18N_LOCALE ||
    'en-GB';
  if (availableLocales.includes(locale)) {
    return locale;
  }
  let language = locale.split('-')[0];
  for (let i = 0; i < availableLocales.length; i++) {
    if (language === availableLocales[i].split('-')[0]) {
      return availableLocales[i];
    }
  }
  return 'en-GB';
};

export const i18n = createI18n({
  locale: getLocale(),
  allowComposition: true,
  globalInjection: true,
  legacy: false,
  fallbackLocale: {
    fr: ['fr-BE'],
    de: ['de-BE'],
    nl: ['nl-BE'],
    en: ['en-GB'],
    default: ['en-GB'],
  },
  numberFormats,
  datetimeFormats,
  messages: {
    [FALLBACK_LOCALE]: enGBMessages,
  },
});

/**
 * Load hit-components locale messages into this package's i18n instance.
 */
export async function loadComponentsLocales(...locales) {
  const toLoad = [
    ...new Set(
      locales
        .filter(Boolean)
        .map((l) => normalizeI18nLocale(l))
        .filter((l) => !loadedLocales.has(l)),
    ),
  ];
  if (!toLoad.length) {
    return;
  }
  const messages = await loadLocaleMessagesFromGlob(localeModules, toLoad);
  toLoad.forEach((locale) => {
    if (messages[locale]) {
      i18n.global.setLocaleMessage(locale, messages[locale]);
    }
    loadedLocales.add(locale);
  });
}

/**
 * Ensure fallback + requested locale are loaded, then activate the locale.
 */
export async function setComponentsLocale(locale) {
  const normalized = normalizeI18nLocale(locale);
  await loadComponentsLocales(FALLBACK_LOCALE, normalized);
  applyI18nLocale(i18n, normalized);
}

export const translate = (key) => {
  if (!key) {
    return '';
  }
  return i18n.global.t(key);
};

export default function installVueI18N(app) {
  // Fire-and-forget for standalone installs; app path awaits setComponentsLocale via initI18N
  setComponentsLocale(getLocale());
  app.use(i18n);
}

export const $d = i18n.global.d;
